import type { Catalog, Classification } from "./index";
import { classify } from "./index";

/**
 * "Next time, 동네마트 is 생활용품": a per-device map from a normalized store
 * key to the category the person chose. Rules sit in front of the catalogue
 * classifier; a category pinned on a record still wins over a rule.
 */
export type StoreCategoryRules = Record<string, string>;

export const STORE_RULES_KEY = "sobidogam:storeCategoryRules";

export function parseStoreRules(raw: string | null): StoreCategoryRules {
	if (!raw) return {};
	try {
		const value = JSON.parse(raw) as unknown;
		if (!value || typeof value !== "object" || Array.isArray(value)) return {};
		return Object.fromEntries(
			Object.entries(value as Record<string, unknown>).filter(
				(entry): entry is [string, string] =>
					typeof entry[1] === "string" && entry[0].length > 0,
			),
		);
	} catch {
		return {};
	}
}

export function setStoreRule(
	rules: StoreCategoryRules,
	storeKey: string,
	categoryId: string,
): StoreCategoryRules {
	if (!storeKey) return rules;
	return { ...rules, [storeKey]: categoryId };
}

export function removeStoreRule(
	rules: StoreCategoryRules,
	storeKey: string,
): StoreCategoryRules {
	const next = { ...rules };
	delete next[storeKey];
	return next;
}

export function classifyWithRules(input: {
	storeKey: string;
	aiCategoryHint: string | null;
	catalog: Catalog;
	rules: StoreCategoryRules;
}):
	| Classification
	| { brandId: string | null; categoryId: string; source: "rule" } {
	const base = classify(input);
	const ruled = input.rules[input.storeKey];
	if (
		ruled &&
		input.catalog.categories.some((category) => category.categoryId === ruled)
	)
		return { brandId: base.brandId, categoryId: ruled, source: "rule" };
	return base;
}
